/**
 * PatternPreview Component - Live pattern preview with test scenarios
 */

import React from 'react'
import { motion } from 'framer-motion'
import { Eye, Code, Database } from 'lucide-react'
import { MacroPattern } from '../../../../../types/macros'

export interface PatternPreviewProps {
  pattern: MacroPattern
  syntax: string
  dataKey?: string
  fallbackValue?: string
  conditionalContent?: string
  elseContent?: string
  showBothScenarios?: boolean
  testData?: Record<string, any>
  className?: string
}

const PatternPreview: React.FC<PatternPreviewProps> = ({
  pattern,
  syntax,
  dataKey,
  fallbackValue,
  conditionalContent,
  elseContent,
  showBothScenarios = true,
  testData,
  className = ""
}) => {
  // Generate test scenarios
  const scenarios = React.useMemo(() => {
    const baseData = testData || {
      first_name: 'John',
      last_name: 'Smith', 
      email: 'john@example.com',
      loyalty_points: 150,
      order_total: 89.99
    }

    return [
      {
        name: 'With Data',
        data: { ...baseData, [dataKey || 'first_name']: baseData[dataKey as keyof typeof baseData] || 'John' },
        icon: Database,
        iconColor: 'text-green-600'
      },
      {
        name: 'Without Data',
        data: { ...baseData, [dataKey || 'first_name']: null },
        icon: Database,
        iconColor: 'text-gray-500'
      }
    ]
  }, [dataKey, testData])

  // Simulate macro rendering
  const renderMacro = (data: Record<string, any>) => {
    if (!syntax || !dataKey) return syntax

    switch (pattern) {
      case MacroPattern.SIMPLE:
        return syntax.replace(`{{${dataKey}}}`, data[dataKey] || '')

      case MacroPattern.FALLBACK:
        const fallback = fallbackValue || 'Guest'
        return syntax.replace(`{{${dataKey}|'${fallback}'}}`, data[dataKey] || fallback)

      case MacroPattern.CONDITIONAL:
        if (data[dataKey]) {
          return conditionalContent || `Welcome back, ${data[dataKey]}!`
        }
        return ''

      case MacroPattern.CONDITIONAL_ELSE:
        if (data[dataKey]) {
          return conditionalContent || `Welcome back, ${data[dataKey]}!`
        }
        return elseContent || 'Welcome!'

      case MacroPattern.ADVANCED:
        // Basic simulation for advanced patterns
        let result = syntax
        Object.keys(data).forEach(key => {
          if (data[key]) {
            result = result.replace(new RegExp(`{{${key}}}`, 'g'), data[key])
            result = result.replace(new RegExp(`{{#if ${key}}}([^}]+){{/if}}`, 'g'), '$1')
          } else {
            result = result.replace(new RegExp(`{{#if ${key}}}([^}]+){{/if}}`, 'g'), '')
          }
        })
        return result

      default:
        return syntax
    }
  }

  if (!syntax) {
    return (
      <div className={`bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-2 text-gray-500">
          <Eye className="w-4 h-4" />
          <span className="text-sm">Preview will appear here as you configure your macro</span>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800 ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Eye className="w-5 h-5 text-green-600" />
        <h3 className="font-semibold text-[#1A2B49] dark:text-white">Live Preview</h3>
      </div>

      {/* Syntax Display */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Code className="w-4 h-4 text-gray-600" />
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Macro Syntax:</span>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <code className="text-sm font-mono text-[#1A2B49] dark:text-green-400 break-all">
            {syntax}
          </code>
        </div>
      </div>

      {/* Scenarios */}
      <div className="space-y-3">
        {showBothScenarios ? (
          scenarios.map((scenario, index) => (
            <motion.div
              key={scenario.name}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center gap-2 mb-2">
                <scenario.icon className={`w-4 h-4 ${scenario.iconColor}`} />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {scenario.name}:
                </span>
              </div>
              <div className="text-sm text-[#1A2B49] dark:text-white">
                {renderMacro(scenario.data) ? (
                  <span className="italic">"{renderMacro(scenario.data)}"</span>
                ) : (
                  <span className="text-gray-500 italic">(no output)</span>
                )}
              </div>
            </motion.div>
          ))
        ) : (
          // Single scenario preview
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-[#1A2B49] dark:text-white">
              <span className="italic">"{renderMacro(scenarios[0].data)}"</span>
            </div>
          </div>
        )}
      </div>

      {/* Pattern Info */}
      <div className="mt-4 pt-3 border-t border-green-200 dark:border-green-800">
        <div className="text-xs text-gray-600 dark:text-gray-400">
          <strong>Pattern:</strong> {pattern.replace('_', ' ').toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase())}
        </div>
      </div>
    </motion.div>
  )
}

export default PatternPreview